import React, { useState, useEffect } from 'react';
import {
	Box,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Button,
	Container,
	TextField,
	Typography,
	Drawer,
	Paper,
	keyframes,
	CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import chatSessionService from '../../../services/chatSessionService';
import { useTalentChat } from '../../../hooks/useTalentChat';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../features/User/userSlice';
import SendIcon from '@mui/icons-material/Send';
import TalentCard from './TalentCard';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../../../logo.png';
import { useNavigate } from 'react-router-dom';
import TypingIndicator from '../../../components/TypingIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useInfiniteQuery } from 'react-query';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import ErrorBoundary from '../../../components/ErrorBoundary';
import Badge from '@mui/material/Badge';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';

// Nämä animaatiot voidaan pitää komponentin ulkopuolella koska ne eivät käytä theme-objektia
const glowAnimation = keyframes`
	0% {
		background-position: 0% 50%;
		box-shadow: 0 0 10px rgba(124, 85, 229, 0.5);
	}
	50% {
		background-position: 100% 50%;
		box-shadow: 0 0 20px rgba(250, 121, 33, 0.5);
	}
	100% {
		background-position: 0% 50%;
		box-shadow: 0 0 10px rgba(124, 85, 229, 0.5);
	}
`;

const pulseAnimation = keyframes`
	0% {
		transform: translateY(-50%) scale(1);
	}
	50% {
		transform: translateY(-50%) scale(1.1);
	}
	100% {
		transform: translateY(-50%) scale(1);
	}
`;

const TalentChatView = () => {
	const [activeSessionId, setActiveSessionId] = useState(null);
	const [message, setMessage] = useState('');
	const [sessionMessages, setSessionMessages] = useState([]);
	const { messages, sendMessage, isLoading, setMessages, resetChat } =
		useTalentChat();
	const user = useSelector(selectUser);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const navigate = useNavigate();
	const [hasStartedChat, setHasStartedChat] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	const inputRef = React.useRef(null);
	const messagesEndRef = React.useRef(null);
	const limit = 20;
	const [savedTalents, setCartItems] = useState([]);
	const [savedDrawerOpen, setCartOpen] = useState(false);
	const [streamError, setStreamError] = useState(null);

	// Yksinkertaistettu animaatio
	const highlightAnimation = keyframes`
		0% {
			transform: translateY(0) scale(1);
			box-shadow: 0 0 10px rgba(124, 85, 229, 0.3);
		}
		50% {
			transform: translateY(0) scale(1.05);
			box-shadow: 0 0 20px rgba(250, 121, 33, 0.5);
		}
		100% {
			transform: translateY(0) scale(1);
			box-shadow: 0 0 10px rgba(124, 85, 229, 0.3);
		}
	`;

	// Haetaan chat-sessiot automaattisesti ilman userId:tä
	const {
		data: sessionsData,
		isLoading: sessionsLoading,
		error: sessionsError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery(
		['chatSessions'],
		({ pageParam = 1 }) =>
			chatSessionService.getChatSessions(
				{}, // Ei filttereitä - backend hoitaa oikeudet
				{
					sortBy: 'lastActivity:desc',
					limit,
					page: pageParam,
				},
			),
		{
			getNextPageParam: (lastPage) => {
				if (lastPage?.data?.page < lastPage?.data?.totalPages) {
					return lastPage.data.page + 1;
				}
				return undefined;
			},
			refetchInterval: 30000,
			refetchOnWindowFocus: true,
		},
	);

	// Yhdistä kaikki sivut
	const chatSessions = React.useMemo(() => {
		return sessionsData?.pages?.flatMap((page) => page.data.results) || [];
	}, [sessionsData]);

	// Käytä joko session viestejä tai uuden chatin viestejä
	const displayMessages = React.useMemo(() => {
		// Jos on aktiivinen sessio ja siinä viestejä, näytä ne
		if (activeSessionId && sessionMessages.length > 0) {
			return sessionMessages;
		}
		// Muuten näytä väliaikaiset viestit
		return messages;
	}, [activeSessionId, sessionMessages, messages]);

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	};

	// Scrollaa alas kun viestit muuttuvat
	useEffect(() => {
		scrollToBottom();
	}, [displayMessages, isLoading, localLoading]);

	// Päivitä matchien käsittely
	useEffect(() => {
		const fetchSessionMessages = async () => {
			if (activeSessionId) {
				try {
					const response =
						await chatSessionService.getChatSession(
							activeSessionId,
						);

					if (response?.data) {
						setSessionMessages(response.data.messages || []);
						setHasStartedChat(true);
					}
				} catch (error) {
					console.error('Failed to fetch session messages:', error);
				}
			}
		};

		fetchSessionMessages();
	}, [activeSessionId]);

	// Muokataan apufunktiota niin että se vain asettaa viestin
	const handlePredefinedMessage = (messageText) => {
		setMessage(messageText);
		// Fokusoidaan tekstikenttään
		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	// Muokataan handleSendMessage-funktiota
	const handleSendMessage = async (e) => {
		if (e) e.preventDefault();
		console.log('handleSendMessage started');
		const messageToSend = message;
		setMessage('');
		if (!messageToSend.trim()) return;
		if (!hasStartedChat) setHasStartedChat(true);

		try {
			const userMessage = { role: 'user', content: messageToSend, type: 'message' };
			setSessionMessages((prev) => [...prev, userMessage]);
			setLocalLoading(true);

			// Käytetään serviceä fetchin sijaan
			const reader = await chatSessionService.sendMessageStream(messageToSend);
			const decoder = new TextDecoder('utf-8');
			let buffer = '';
			let eventType = null;
			let errorOccurred = false;

			while (true) {
				let result;
				try {
					result = await reader.read();
				} catch (err) {
					console.error('SSE reader.read() error:', err);
					setLocalLoading(false);
					errorOccurred = true;
					setStreamError('Yhteys katkesi (reader.read error)');
					if (reader && reader.cancel) { try { await reader.cancel(); } catch {} }
					break;
				}
				if (!result) break;
				const { value, done } = result;
				if (done) break;
				buffer += decoder.decode(value, { stream: true });

				let lines = buffer.split('\n');
				buffer = lines.pop(); // viimeinen voi olla keskeneräinen

				for (let line of lines) {
					if (line.startsWith('event:')) {
						eventType = line.replace('event:', '').trim();
						console.log('SSE eventType:', eventType);
					}
					if (line.startsWith('data:')) {
						let data = line.replace('data:', '').trim();
						try { data = JSON.parse(data); } catch {}
						console.log('SSE data:', { eventType, data });
						setSessionMessages((prev) => {
							const updated = [...prev, { role: eventType === 'delta' ? 'assistant' : 'system', type: eventType, content: data }];
							console.log('sessionMessages updated:', updated);
							return updated;
						});
						if (
							eventType === 'delta' &&
							typeof data === 'object' &&
							data &&
							Object.prototype.hasOwnProperty.call(data, 'status') &&
							data.status === 'finished_successfully'
						) {
							setLocalLoading(false);
						}
						if (
							eventType === 'message' &&
							typeof data === 'object' &&
							data &&
							Object.prototype.hasOwnProperty.call(data, 'type') &&
							data.type === 'message_stream_complete'
						) {
							setLocalLoading(false);
						}
					}
				}
			}
		} catch (err) {
			console.error('SSE stream error:', err);
			setLocalLoading(false);
			errorOccurred = true;
			setStreamError('Yhteys katkesi (SSE stream error)');
			if (reader && reader.cancel) { try { await reader.cancel(); } catch {} }
		}
		setLocalLoading(false);

		try {
			setTimeout(() => {
				if (inputRef.current) inputRef.current.focus();
			}, 100);
		} catch (error) {
			console.error('Error setting focus:', error);
		}
	};

	const handleNewChat = async () => {
		console.log('Starting new chat...');
		setActiveSessionId(null);
		setSessionMessages([]);
		setMessages([]);
		resetChat();

		// Fokusoi tekstikenttään heti
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus();
				// Varmistetaan että tekstikenttä on näkyvissä
				inputRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		}, 100);

		try {
			const response = await chatSessionService.createChatSession();
			if (response?.data) {
				console.log('New chat session created:', response.data);
				setActiveSessionId(response.data.sessionId);
			}
		} catch (error) {
			console.error('Error creating new chat session:', error);
		}
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString('fi-FI', {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	// Lisää näppäimistön käsittely
	const handleKeyPress = (e) => {
		console.log('Key pressed:', e.key, 'Shift:', e.shiftKey);
		if (e.key === 'Enter' && !e.shiftKey) {
			console.log('Enter pressed without shift, submitting');
			e.preventDefault();
			handleSendMessage(e);
		}
	};

	// Update sidebar state when screen size changes
	useEffect(() => {
		if (!isMobile) {
			setSidebarOpen(true);
		} else {
			setSidebarOpen(false);
		}
	}, [isMobile]);

	// Hae aktiivisen session tiedot
	const activeSession = chatSessions.find(
		(session) => session.id === activeSessionId,
	);

	const handleAddToCart = (talent) => {
		if (!savedTalents.some((item) => item.id === talent.id)) {
			setCartItems((prev) => [...prev, talent]);
		}
	};

	const handleRemoveFromCart = (talentId) => {
		setCartItems((prev) => prev.filter((item) => item.id !== talentId));
	};

	const renderWelcomeMessage = () => (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				height: '100%',
				textAlign: 'center',
				px: 2,
			}}
		>
			<Typography variant="h4" gutterBottom>
				Tervetuloa Lenz Platformille
			</Typography>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					bgcolor: 'background.paper',
					borderRadius: 2,
					maxWidth: 700,
				}}
			>
				<Typography gutterBottom variant="h5">
					Nopein tapa löytää oikeat tekijät
				</Typography>
				<Typography variant="body1" sx={{ mb: 4 }}>
					Kuvaile tarpeesi, ja tekoälymme hoitaa loput. Lenz yhdistää
					yrityksesi tarpeet täydellisesti sopiviin asiantuntijoihin
					hyödyntäen tekoälyä.
				</Typography>

				{/* Lisätään valmiit keskustelunaloitukset */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
						gap: 2,
						mb: 4,
					}}
				>
					<Button
						variant="outlined"
						onClick={() =>
							handlePredefinedMessage('Mikä on Lenz Platform?')
						}
						sx={{
							p: 2,
							textAlign: 'left',
							justifyContent: 'flex-start',
							borderColor: 'rgba(124, 85, 229, 0.3)',
							transition: 'all 0.2s ease-in-out',
							'&:hover': {
								borderColor: 'primary.main',
								bgcolor: 'rgba(124, 85, 229, 0.04)',
								transform: 'translateY(-2px)',
							},
						}}
					>
						<Box>
							<Typography variant="subtitle1" color="primary">
								Mikä on Lenz Platform?
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Kerro lisää palvelusta
							</Typography>
						</Box>
					</Button>

					<Button
						variant="outlined"
						onClick={() =>
							handlePredefinedMessage(
								'Mitä apua Lenz Platformista on meille?',
							)
						}
						sx={{
							p: 2,
							textAlign: 'left',
							justifyContent: 'flex-start',
							borderColor: 'rgba(124, 85, 229, 0.3)',
							'&:hover': {
								borderColor: 'primary.main',
								bgcolor: 'rgba(124, 85, 229, 0.04)',
							},
						}}
					>
						<Box>
							<Typography variant="subtitle1" color="primary">
								Mitä apua Lenz Platformista on meille?
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Tustustu hyötyihin
							</Typography>
						</Box>
					</Button>
				</Box>

				<Typography variant="h6" color="primary">
					🚀 Kokeile nyt – löydä potentiaali hetkessä.
				</Typography>
			</Paper>
		</Box>
	);

	// Kootaan peräkkäiset assistant/delta-viestit yhdeksi viestiksi
	const mergedMessages = [];
	for (let i = 0; i < sessionMessages.length; i++) {
		const msg = sessionMessages[i];
		if (msg.role === 'assistant' && msg.type === 'delta') {
			let content = '';
			let j = i;
			while (j < sessionMessages.length && sessionMessages[j].role === 'assistant' && sessionMessages[j].type === 'delta' && typeof sessionMessages[j].content === 'string') {
				content += sessionMessages[j].content;
				j++;
			}
			mergedMessages.push({ role: 'assistant', type: 'delta', content });
			i = j - 1;
		} else {
			mergedMessages.push(msg);
		}
	}

	return (
		<Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50', position: 'relative' }}>
			{/* Mobile-only menu button - positioned above everything else */}
			{isMobile && (
				<IconButton
					onClick={() => setSidebarOpen(true)}
					sx={{
						position: 'fixed',
						left: 10,
						top: 10,
						zIndex: 1500,
						backgroundColor: 'white',
						boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
						width: 45,
						height: 45,
						'&:hover': {
							backgroundColor: 'white',
						},
					}}
					aria-label="Open menu"
				>
					<MenuIcon fontSize="medium" />
				</IconButton>
			)}
			
			{/* Rest of the layout */}
			<Drawer
				variant={isMobile ? 'temporary' : 'permanent'}
				open={sidebarOpen}
				onClose={() => setSidebarOpen(false)}
				sx={{
					width: 280,
					flexShrink: 0,
					zIndex: 1300,
					'& .MuiDrawer-paper': {
						width: 280,
						boxSizing: 'border-box',
						border: 'none',
						bgcolor: 'grey.100',
						zIndex: 1300,
						boxShadow: isMobile ? '4px 0px 10px rgba(0,0,0,0.1)' : 'none',
					},
				}}
				ModalProps={{
					keepMounted: true,
				}}
			>
				{/* Header */}
				<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
						<Box
							component="img"
							sx={{ width: 53, cursor: 'pointer' }}
							alt="logo"
							src={logo}
							onClick={() => navigate('/')}
						/>
						<Typography
							variant="h4"
							noWrap
							sx={{ ml: 1, color: theme.palette.primary.main }}
						>
							Dynamis Ai
						</Typography>
					</Box>

					<Button
						startIcon={<AddIcon />}
						onClick={handleNewChat}
						variant="contained"
						fullWidth
						sx={{
							py: 1,
							textTransform: 'none',
							boxShadow: 'none',
							backgroundColor: 'white',
							border: '1px solid rgba(0, 0, 0, 0.12)',
							color: 'text.primary',
							'&:hover': {
								backgroundColor: 'rgba(124, 85, 229, 0.04)',
								borderColor: '#7c55e5',
								color: '#7c55e5',
							},
						}}
					>
						Uusi keskustelu
					</Button>
				</Box>

				{/* Scrollattava lista */}
				<Box
					sx={{
						flex: 1,
						overflow: 'auto',
						height: '100%',
						'&::-webkit-scrollbar': {
							width: '8px',
						},
						'&::-webkit-scrollbar-track': {
							backgroundColor: 'transparent',
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: 'rgba(0, 0, 0, 0.1)',
							borderRadius: '4px',
						},
					}}
					onScroll={(e) => {
						const element = e.target;
						// Tarkistetaan että elementti on HTMLElement
						if (element instanceof HTMLElement) {
							const bottom =
								element.scrollHeight - element.scrollTop <=
								element.clientHeight + 100; // 100px ennen pohjaa
							if (bottom && hasNextPage && !isFetchingNextPage) {
								console.log('Fetching next page...'); // Debug
								fetchNextPage();
							}
						}
					}}
				>
					<List>
						{sessionsLoading ? (
							<ListItem>
								<Box
									display="flex"
									justifyContent="center"
									width="100%"
									py={2}
								>
									<CircularProgress />
								</Box>
							</ListItem>
						) : sessionsError ? (
							<ListItem>
								<Typography color="error">
									Virhe ladattaessa keskusteluja
								</Typography>
							</ListItem>
						) : chatSessions.length === 0 ? (
							<ListItem>
								<Typography color="text.secondary">
									Ei aktiivisia keskusteluja
								</Typography>
							</ListItem>
						) : (
							chatSessions.map((session) => (
								<ListItem
									key={session.id}
									component="div"
									onClick={() => {
										setActiveSessionId(session.id);
										if (isMobile) {
											setSidebarOpen(false);
										}
									}}
									sx={{
										width: '100%',
										textAlign: 'left',
										border: 'none',
										background:
											session.id === activeSessionId
												? 'rgba(124, 85, 229, 0.08)'
												: 'none',
										cursor: 'pointer',
										p: 2,
										borderLeft:
											session.id === activeSessionId
												? '4px solid #7c55e5'
												: 'none',
										'& .MuiTypography-root': {
											color:
												session.id === activeSessionId
													? '#7c55e5'
													: 'inherit',
										},
										'&:hover': {
											bgcolor: 'rgba(124, 85, 229, 0.04)',
											transition: 'all 0.3s ease',
										},
									}}
								>
									<ListItemText
										primary={
											session.title ||
											'Nimetön keskustelu'
										}
										secondary={
											<Box>
												<Typography
													variant="body2"
													color="text.secondary"
												>
													{format(
														new Date(
															session.lastActivity,
														),
														'dd.MM.yyyy HH:mm',
														{ locale: fi },
													)}
												</Typography>
												{session.messageCount > 0 && (
													<Typography
														variant="caption"
														color="primary"
														component="span"
														sx={{ ml: 1 }}
													>
														{session.messageCount}{' '}
														viestiä
													</Typography>
												)}
											</Box>
										}
									/>
								</ListItem>
							))
						)}

						{isFetchingNextPage && (
							<Box display="flex" justifyContent="center" p={2}>
								<CircularProgress size={24} />
							</Box>
						)}
					</List>
				</Box>
			</Drawer>

			{/* Pääsisältö */}
			<Box
				sx={{
					flexGrow: 1,
					display: 'flex',
					flexDirection: 'column',
					height: '100vh',
					overflow: 'hidden',
					position: 'relative',
				}}
			>
				{/* Tallennetut asiantuntijat -nappi */}
				<Box
					sx={{
						position: 'fixed',
						right: 20,
						top: { xs: 10, sm: 20 },
						zIndex: 1100,
					}}
				>
					<IconButton
						onClick={() => setCartOpen(true)}
						sx={{
							bgcolor: 'white',
							boxShadow: 2,
							width: 45,
							height: 45,
							'&:hover': {
								bgcolor: 'rgba(255, 255, 255, 0.9)',
							},
						}}
					>
						<Badge
							badgeContent={savedTalents.length}
							color="primary"
						>
							<BookmarkIcon />
						</Badge>
					</IconButton>
				</Box>

				{/* Tallennetut asiantuntijat -drawer */}
				<Drawer
					anchor="right"
					open={savedDrawerOpen}
					onClose={() => setCartOpen(false)}
					sx={{
						zIndex: 1200,
						'& .MuiDrawer-paper': {
							width: { xs: '90%', sm: 500 },
							boxSizing: 'border-box',
						},
					}}
				>
					<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								mb: 2,
							}}
						>
							<Typography variant="h6">
								Tallennetut asiantuntijat ({savedTalents.length}
								)
							</Typography>
							<IconButton onClick={() => setCartOpen(false)}>
								<CloseIcon />
							</IconButton>
						</Box>
					</Box>
					<Box sx={{ p: 2, overflow: 'auto' }}>
						{savedTalents.length === 0 ? (
							<Typography color="text.secondary" align="center">
								Ei vielä tallennettuja asiantuntijoita
							</Typography>
						) : (
							savedTalents.map((talent) => (
								<Box
									key={talent.id}
									sx={{ mb: 2, position: 'relative' }}
								>
									<IconButton
										onClick={() =>
											handleRemoveFromCart(talent.id)
										}
										sx={{
											position: 'absolute',
											right: 8,
											top: 45,
											zIndex: 1,
											bgcolor: 'background.paper',
										}}
									>
										<BookmarkIcon color="primary" />
									</IconButton>
									<TalentCard
										talent={talent}
										match={{}}
										isSelected={false}
										sessionId={activeSessionId}
										showInterviewButton={false}
										isAuthenticated={!!user}
										mode="sidebar"
									/>
								</Box>
							))
						)}
					</Box>
				</Drawer>

				{/* Mobiili title bar - remove the menu button */}
				{isMobile && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							px: 2,
							py: 1.5,
							borderBottom: '1px solid',
							borderColor: 'divider',
							bgcolor: 'rgba(255, 255, 255, 0.9)',
							backdropFilter: 'blur(10px)',
							position: 'sticky',
							top: 0,
							zIndex: 1000,
							boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
							height: 56,
							paddingLeft: '60px', // Make room for the fixed menu button
						}}
					>
						<Typography
							variant="subtitle1"
							sx={{
								flex: 1,
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								fontWeight: 500,
								color: 'text.primary',
							}}
						>
							{activeSession?.title || 'Uusi keskustelu'}
						</Typography>
					</Box>
				)}

				{/* Chat-alue (scrollattava) */}
				<Box
					sx={{
						flex: 1,
						overflowY: 'auto',
						overflowX: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						'&::-webkit-scrollbar': {
							width: '8px',
							position: 'absolute',
							right: 0,
						},
						'&::-webkit-scrollbar-track': {
							background: 'transparent',
						},
						'&::-webkit-scrollbar-thumb': {
							background: 'rgba(0, 0, 0, 0.2)',
							borderRadius: '4px',
						},
					}}
				>
					{/* Sisältö keskitetään */}
					<Container
						maxWidth="lg"
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							position: 'relative',
							px: { xs: 2, sm: 4 },
							py: 3,
						}}
					>
						{/* DEBUG: Show raw sessionMessages */}
						<Box sx={{ mb: 2, p: 1, bgcolor: '#eee', borderRadius: 1, fontSize: 12 }}>
							<strong>DEBUG sessionMessages:</strong>
							<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(sessionMessages, null, 2)}</pre>
						</Box>
						{!hasStartedChat && mergedMessages.length === 0 ? (
							renderWelcomeMessage()
						) : (
							<>
								{mergedMessages.map((msg, index) => {
									// User-viesti
									if (msg.role === 'user') {
										return (
											<Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2 }}>
												<Box sx={{ maxWidth: '85%', minWidth: '20%', bgcolor: 'rgba(124, 85, 229, 0.1)', border: '1px solid rgba(124, 85, 229, 0.2)', color: 'text.primary', p: 2, borderRadius: 2, boxShadow: 'none' }}>
													<Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
														User - message
													</Typography>
													<Typography>{msg.content}</Typography>
												</Box>
											</Box>
										);
									}
									// Assistant-viesti (koottu)
									if (msg.role === 'assistant' && msg.type === 'delta') {
										return (
											<Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', mb: 2 }}>
												<Box sx={{ maxWidth: '85%', minWidth: '20%', bgcolor: 'rgba(250, 121, 33, 0.05)', border: '1px solid rgba(250, 121, 33, 0.1)', color: 'text.primary', p: 2, borderRadius: 2, boxShadow: 'none' }}>
													<Typography variant="caption" color="primary" sx={{ mb: 1 }}>
														Assistant
													</Typography>
													<Typography>{msg.content}</Typography>
												</Box>
											</Box>
										);
									}
									// Status-viesti
									if (msg.type === 'status') {
										return (
											<Box key={index} sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 2 }}>
												<Box sx={{ maxWidth: '60%', bgcolor: '#f5f5f5', border: '1px solid #ddd', color: 'text.secondary', p: 1.5, borderRadius: 2, fontStyle: 'italic', textAlign: 'center' }}>
													<Typography variant="caption" color="text.secondary">
														Status
													</Typography>
													<Typography>{typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}</Typography>
												</Box>
											</Box>
										);
									}
									// Muut eventit (conversation, message_stream_complete, jne)
									return (
										<Box key={index} sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 2 }}>
											<Box sx={{ maxWidth: '60%', bgcolor: '#e3e3e3', border: '1px solid #bbb', color: 'text.secondary', p: 1.5, borderRadius: 2, fontSize: 13, textAlign: 'center' }}>
												<Typography variant="caption" color="text.secondary">
													{msg.type}
												</Typography>
												<Typography>{typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}</Typography>
											</Box>
										</Box>
									);
								})}
								{(isLoading || localLoading) && (
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'flex-start',
											width: '100%',
										}}
									>
										<Box
											sx={{
												maxWidth: '85%',
												minWidth: '20%',
												bgcolor:
													'rgba(250, 121, 33, 0.05)',
												border: '1px solid rgba(250, 121, 33, 0.1)',
												borderRadius: 2,
												boxShadow: 'none',
											}}
										>
											<TypingIndicator />
										</Box>
									</Box>
								)}
								<Box ref={messagesEndRef} sx={{ height: 1 }} />
							</>
						)}
						{streamError && (
							<Box sx={{ bgcolor: '#ffebee', color: '#b71c1c', p: 2, borderRadius: 2, mb: 2, textAlign: 'center' }}>
								<Typography variant="body2">{streamError}</Typography>
							</Box>
						)}
					</Container>
				</Box>

				{/* Viestin lähetys (sticky alapalkki) */}
				<Box
					component="form"
					onSubmit={handleSendMessage}
					sx={{
						position: 'sticky',
						bottom: 0,
						borderTop: '1px solid',
						borderColor: 'divider',
						bgcolor: 'rgba(255, 255, 255, 0.9)',
						backdropFilter: 'blur(10px)',
						p: { xs: 2, sm: 3 },
						zIndex: 1100,
						boxShadow: '0 -1px 2px rgba(0, 0, 0, 0.05)',
					}}
				>
					<Container maxWidth="lg">
						<Box
							sx={{
								display: 'flex',
								gap: 2,
								alignItems: 'center',
							}}
						>
							<TextField
								inputRef={inputRef}
								fullWidth
								multiline
								maxRows={4}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Kirjoita viesti..."
								disabled={isLoading || localLoading}
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: 2,
										bgcolor: 'background.paper',
									},
								}}
							/>
							<Button
								type="submit"
								variant="contained"
								disabled={
									isLoading || localLoading || !message.trim()
								}
								sx={{
									minWidth: 'auto',
									width: 48,
									height: 48,
									borderRadius: 2,
								}}
							>
								<SendIcon />
							</Button>
						</Box>
					</Container>
				</Box>
			</Box>
		</Box>
	);
};

const TalentChatViewWithErrorBoundary = () => (
	<ErrorBoundary>
		<TalentChatView />
	</ErrorBoundary>
);

TalentChatViewWithErrorBoundary.displayName = 'TalentChatViewWithErrorBoundary';

export default TalentChatViewWithErrorBoundary;
